import { useMemo, useState } from 'react';
import AttachmentTile, { AttachmentTileState } from 'components/Widget/AttachmentTile';
import Lightbox, { type LightboxItem } from 'components/Widget/Lightbox';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { UploadTracker } from 'utils';
import { VITE_FORGE_URL } from '@constants';
import * as S from './styles';

const isTextPreviewable = (contentType: string) =>
	contentType.startsWith('text/') ||
	contentType === 'application/json' ||
	contentType === 'application/javascript' ||
	contentType === 'application/xml';

const isPdfPreviewable = (contentType: string) => contentType === 'application/pdf';

interface AttachmentStripProps {
	attachments: Attachment[];
	loadingAttachments?: UploadTracker[];
	onRemove?: (storagePath: string) => void;
	onCancel?: (uniqueId: string) => void;
}

const AttachmentStrip = ({
	attachments,
	loadingAttachments,
	onRemove,
	onCancel,
}: AttachmentStripProps) => {
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);

	// Merge completed + loading attachments into a single ordered list
	const allAttachments = useMemo(() => {
		const loading = (loadingAttachments || []).map((tracker) => ({
			...tracker,
			storagePath: `loading-${tracker.uniqueId}`,
		}));
		return [...attachments, ...loading];
	}, [attachments, loadingAttachments]);

	// Build gallery items and a lookup from storagePath to gallery index
	const { galleryItems, galleryIndexByPath } = useMemo(() => {
		const items: LightboxItem[] = [];
		const indexMap = new Map<string, number>();

		for (const attachment of allAttachments) {
			const isLoading = attachment.storagePath.startsWith('loading-');
			const isImage = attachment.contentType.startsWith('image/');
			const isText = isTextPreviewable(attachment.contentType);
			const isPdf = isPdfPreviewable(attachment.contentType);

			if (!isImage && !isText && !isPdf) continue;

			// Skip loading non-image attachments (no meaningful preview during upload)
			if (isLoading && !isImage) continue;

			const url = isLoading
				? loadingAttachments?.find(
						(a) => a.uniqueId === attachment.storagePath.replace('loading-', '')
				  )?.previewUrl
				: `${VITE_FORGE_URL}/attachment/${encodeURIComponent(attachment.storagePath)}`;

			if (!url) continue;

			indexMap.set(attachment.storagePath, items.length);

			if (isImage) {
				items.push({ type: 'image', url });
			} else if (isText) {
				items.push({ type: 'text', url, filename: attachment.filename, size: attachment.size });
			} else {
				items.push({ type: 'pdf', url, filename: attachment.filename });
			}
		}

		return { galleryItems: items, galleryIndexByPath: indexMap };
	}, [allAttachments, loadingAttachments]);

	if (!allAttachments.length) return null;

	return (
		<>
			<S.Base
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{allAttachments.map((attachment) => {
					const isLoading = attachment.storagePath.startsWith('loading-');
					const tracker = isLoading
						? loadingAttachments?.find(
								(a) => a.uniqueId === attachment.storagePath.replace('loading-', '')
						  )
						: undefined;
					const galleryIdx = galleryIndexByPath.get(attachment.storagePath);

					return (
						<AttachmentTile
							key={attachment.storagePath}
							attachment={attachment}
							onDelete={
								isLoading
									? tracker && onCancel
										? () => onCancel(tracker.uniqueId)
										: undefined
									: onRemove
									? () => onRemove(attachment.storagePath)
									: undefined
							}
							state={isLoading ? AttachmentTileState.LOADING : AttachmentTileState.REGULAR}
							previewUrl={tracker?.previewUrl}
							onPreview={galleryIdx != null ? () => setPreviewIndex(galleryIdx) : undefined}
						/>
					);
				})}
			</S.Base>
			{previewIndex != null && (
				<Lightbox
					items={galleryItems}
					startIndex={previewIndex}
					onClose={() => setPreviewIndex(null)}
				/>
			)}
		</>
	);
};

export default AttachmentStrip;
